/**
 * Review step component
 * Step 4 of job wizard - review and confirm
 */

import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Card, Descriptions, Typography, Alert, Space, Tag, Divider, Row, Col } from 'antd';
import {
  SearchOutlined,
  EnvironmentOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { GridPreview } from './GridPreview';
import type { CreateJobInput } from '@/types/job';
import type { GridConfig } from '@/types/grid';
import { calculateArea, generateGridPreview, zoomToCellSize } from '@/utils/mapUtils';
import { formatNumber, formatCoordinates } from '@/utils/formatters';

const { Title, Text, Paragraph } = Typography;

export function ReviewStep() {
  const { watch } = useFormContext<CreateJobInput>();

  const formData = watch();
  const {
    searchQuery,
    name,
    bounds,
    polygon,
    selectionType,
    zoomLevel = 16,
    maxResultsPerCell = 60,
    includeDetails,
  } = formData;

  const gridConfig: GridConfig = useMemo(() => ({
    zoomLevel,
    cellSizeKm: zoomToCellSize(zoomLevel),
    overlapPercent: 10,
  }), [zoomLevel]);

  const gridPreview = useMemo(() => {
    if (!bounds) return null;
    return generateGridPreview(bounds, gridConfig, polygon);
  }, [bounds, gridConfig, polygon]);

  const areaKm2 = bounds ? calculateArea(bounds) : 0;
  const estimatedMinutes = gridPreview ? Math.ceil((gridPreview.totalCells * 3) / 60) : 0;
  const estimatedPlaces = gridPreview
    ? gridPreview.totalCells * Math.floor(maxResultsPerCell * 0.3)
    : 0;

  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        Review your job configuration
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 24 }}>
        Please review the settings below before creating the job.
      </Paragraph>

      <Row gutter={24}>
        <Col xs={24} lg={14}>
          {/* Summary cards */}
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* Search query */}
            <Card size="small">
              <Space>
                <SearchOutlined style={{ fontSize: 20, color: 'var(--color-primary)' }} />
                <div>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Search Query
                  </Text>
                  <Text strong style={{ fontSize: 16 }}>
                    {searchQuery}
                  </Text>
                  {name && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                      Job name: {name}
                    </Text>
                  )}
                </div>
              </Space>
            </Card>

            {/* Location */}
            <Card size="small">
              <Space>
                <EnvironmentOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                <div>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Search Area
                  </Text>
                  <Space size={8}>
                    <Text strong>~{areaKm2.toFixed(1)} km²</Text>
                    {selectionType === 'polygon' && (
                      <Tag color="purple">Freehand</Tag>
                    )}
                  </Space>
                  {bounds && (
                    <Text type="secondary" style={{ display: 'block', fontSize: 11 }}>
                      {formatCoordinates(bounds.north, bounds.west)} to{' '}
                      {formatCoordinates(bounds.south, bounds.east)}
                    </Text>
                  )}
                </div>
              </Space>
            </Card>

            {/* Configuration */}
            <Card size="small">
              <Space>
                <SettingOutlined style={{ fontSize: 20, color: '#52c41a' }} />
                <div>
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                    Configuration
                  </Text>
                  <Space size={8} wrap>
                    <Tag>Zoom: {zoomLevel}</Tag>
                    <Tag>Max {maxResultsPerCell}/cell</Tag>
                    <Tag color={includeDetails ? 'green' : 'default'}>
                      {includeDetails ? 'With details' : 'Basic info'}
                    </Tag>
                  </Space>
                </div>
              </Space>
            </Card>

            {/* Estimates */}
            <Card size="small">
              <Descriptions
                column={2}
                size="small"
                labelStyle={{ color: 'var(--color-text-secondary)' }}
              >
                <Descriptions.Item label="Grid Cells">
                  <Text strong>{formatNumber(gridPreview?.totalCells || 0)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label="Grid Size">
                  {gridPreview?.rows} x {gridPreview?.columns}
                </Descriptions.Item>
                <Descriptions.Item label="Est. Duration">
                  <Space>
                    <ClockCircleOutlined />
                    <Text strong>~{estimatedMinutes} minutes</Text>
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label="Est. Places">
                  <Space>
                    <AppstoreOutlined />
                    <Text strong>~{formatNumber(estimatedPlaces)}</Text>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Space>

          {/* Warnings */}
          {gridPreview && gridPreview.totalCells > 1000 && (
            <Alert
              type="warning"
              message="Large job detected"
              description={`This job has ${formatNumber(gridPreview.totalCells)} cells and may take ${estimatedMinutes} minutes or more to complete. You can pause and resume at any time.`}
              style={{ marginTop: 16 }}
              showIcon
            />
          )}

          {/* Ready message */}
          <Alert
            type="success"
            message="Ready to create"
            description="Click 'Create Job' to start scraping. The job will begin processing immediately."
            style={{ marginTop: 16 }}
            showIcon
            icon={<CheckCircleOutlined />}
          />
        </Col>

        {/* Map preview */}
        <Col xs={24} lg={10}>
          {bounds && (
            <Card size="small" title="Area Preview">
              <GridPreview
                bounds={bounds}
                zoomLevel={zoomLevel}
                height={350}
                showGrid
                showStats={false}
                polygon={polygon}
              />
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}

export default ReviewStep;
